/**
 * Durchläuft Original- und Klon-DOM rekursiv und ersetzt bei geklonten Elementen
 * ein berechnetes display:none durch display:block, damit ausgeblendete Inhalte
 * im Druck-HTML sichtbar gemacht werden können.
 */
function forceDisplayNoneToBlock(originalNode, clonedNode) {
    if (!(originalNode instanceof Element) || !(clonedNode instanceof Element)) {
        return;
    }

    const computedStyle = window.getComputedStyle(originalNode);

    if (computedStyle.display === "none") {
        clonedNode.style.setProperty("display", "block", "important");
    }

    const originalChildren = Array.from(originalNode.children);
    const clonedChildren = Array.from(clonedNode.children);

    for (let i = 0; i < originalChildren.length; i++) {
        const originalChild = originalChildren[i];
        const clonedChild = clonedChildren[i];

        if (!clonedChild) continue;

        forceDisplayNoneToBlock(originalChild, clonedChild);
    }
}

/**
 * Verkleinert alle Bilder im geklonten Inhalt (<img>, auch mit SVG-Quelle,
 * sowie Inline-<svg>) auf den angegebenen Faktor (z. B. 0.6 = 60 %).
 * Die Skalierung erfolgt über max-width in Prozent der verfügbaren Breite;
 * die Höhe wird automatisch angepasst, damit das Seitenverhältnis erhalten
 * bleibt. Feste Inline-Größen (z. B. 1920x1280px) werden dadurch überschrieben.
 */
function scaleImages(root, scale) {
    if (!scale || scale <= 0 || scale >= 1) return;

    const percent = `${scale * 100}%`;

    root.querySelectorAll("img, svg").forEach((el) => {
        const isSvg = el.tagName.toLowerCase() === "svg";

        // Verschachtelte SVG-Elemente nicht doppelt skalieren
        if (isSvg && el.parentElement && el.parentElement.closest("svg")) {
            return;
        }

        el.style.setProperty("max-width", percent, "important");

        // Inline-SVG ohne viewBox hat kein festes Seitenverhältnis -> Höhe nicht auf auto setzen
        if (!isSvg || el.hasAttribute("viewBox")) {
            el.style.setProperty("height", "auto", "important");
        }
    });
}

/**
 * Markiert die Abschnitte mit den angegebenen IDs (die ID sitzt auf der
 * Überschrift, daher wird das umschließende Abschnitts-Element markiert),
 * sodass sie im Druck nicht über zwei Seiten getrennt werden. Passt ein
 * Abschnitt nicht mehr vollständig auf die Restseite, beginnt er auf der
 * nächsten Seite.
 */
function markSectionsKeepTogether(root, ids, keepTogetherClass) {
    ids.forEach((id) => {
        const heading = root.querySelector(`[id="${id}"]`);
        if (!heading) return;

        const section = heading.parentElement;
        if (!section || section === root) {
            heading.classList.add(keepTogetherClass);
            return;
        }

        section.classList.add(keepTogetherClass);
    });
}

/**
 * Markiert Abschnitte gemäß den übergebenen Regeln, sodass sie im Druck auf
 * einer neuen Seite beginnen (die ID sitzt auf der Überschrift, daher wird das
 * umschließende Abschnitts-Element markiert).
 *
 * Jede Regel hat die Form:
 *   { id: "abschnitts-id", onlyIfAnyExists?: ["id-a", "id-b"] }
 *
 * Ohne onlyIfAnyExists beginnt der Abschnitt immer auf einer neuen Seite.
 * Mit onlyIfAnyExists gilt der Umbruch nur, wenn mindestens einer der dort
 * genannten Abschnitte im gedruckten Inhalt vorhanden ist (sonst wäre der
 * Abschnitt der erste Inhalt und ein Umbruch würde eine unnötige Leerseite
 * bzw. einen unnötigen Abstand erzeugen).
 */
function markSectionsForPageBreak(root, rules, breakBeforeClass) {
    const sectionExists = (id) => Boolean(root.querySelector(`[id="${id}"]`));

    rules.forEach(({ id, onlyIfAnyExists }) => {
        const heading = root.querySelector(`[id="${id}"]`);
        if (!heading) return;

        if (
            Array.isArray(onlyIfAnyExists) &&
            onlyIfAnyExists.length > 0 &&
            !onlyIfAnyExists.some(sectionExists)
        ) {
            return;
        }

        const section = heading.parentElement;
        if (!section || section === root) {
            heading.classList.add(breakBeforeClass);
            return;
        }

        section.classList.add(breakBeforeClass);
    });
}

/**
 * Ermittelt den Titel für den Druck (= Dateiname-Vorschlag beim PDF-Speichern):
 * Seitentitel plus data-id des ersten Elements von titleInfoSelector.
 * Leerzeichen werden durch _ ersetzt.
 */
function resolvePrintTitle({
                               title = document.title || "Print",
                               titleInfoSelector = ".weekly__info",
                               titleInfoAttribute = "data-id",
                               titleSeparator = "_"
                           } = {}) {
    const infoValue = document
        .querySelector(titleInfoSelector)
        ?.getAttribute(titleInfoAttribute)
        ?.trim();

    return (infoValue ? `${title}${titleSeparator}${infoValue}` : title)
        .trim()
        .replace(/\s+/g, "_");
}

/* ========================================================================== *
 *  Firefox- und Safari-Korrekturen
 *
 *  Chrome rendert das Druck-Dokument korrekt. Firefox weicht an fünf Stellen ab,
 *  die auf bekannte Gecko-/macOS-Bugs bzw. Engine-Unterschiede zurückgehen:
 *
 *  1. Verläufe mit Transparenz (z. B. radial-gradient(rgba(0,0,0,.02), transparent))
 *     werden beim Drucken unter macOS (Quartz) ohne Alpha-Kanal ausgegeben und
 *     erscheinen als schwarze Fläche (Mozilla-Bugs 1851954, 2000439, 1529152).
 *     -> Solche Verlaufsebenen werden vor dem Druck in ein PNG mit Alphakanal
 *        gerastert; PNG-Transparenz gibt Firefox korrekt aus.
 *
 *  2. Absolut positionierte Elemente (z. B. die Nummern-Kreise der Aufgaben)
 *     werden falsch platziert, wenn ihr Containing Block über mehrere Seiten
 *     läuft: Firefox ignoriert den Leerraum, der durch Seitenumbrüche entsteht
 *     (Mozilla-Bugs 1187457, 1158666).
 *     -> Jedes betroffene Element bekommt ein nahes, nicht umbrechendes Elternteil
 *        als Containing Block (position: relative) und wird mit den im Layout
 *        gemessenen Koordinaten fixiert. Optisch ändert sich dadurch nichts.
 *
 *  3. Ligaturen (fi, fl, …) werden von Firefox unter macOS ohne korrekte
 *     Unicode-Zuordnung eingebettet; im PDF wird z. B. aus „häufigste“ beim
 *     Kopieren/Suchen „häuegste“.
 *     -> Standard-Ligaturen werden im Druck-Dokument abgeschaltet.
 *
 *  4. „An Seitenbreite anpassen“ rechnet in Firefox mit einer etwas zu großen
 *     Dokumentbreite; alles wird ~0,8 % kleiner als in Chrome gedruckt.
 *     -> Der Chrome-Maßstab wird selbst berechnet und per CSS zoom gesetzt.
 *
 *  5. Aufzählungspunkte (disc) setzt Firefox mit einer eigenen Bullet-Schrift,
 *     die jede Listenzeile etwas erhöht; der Punkt ist zudem größer.
 *     -> Der Punkt wird durch denselben Kreis ersetzt, den Chrome zeichnet.
 *
 *  Alle Messungen dafür laufen in einem druckgleichen Zustand des Iframes
 *  (Seitenbreite, @media print aktiv), damit Werte aus dem Drucklayout und
 *  nicht aus dem Bildschirmlayout stammen.
 *
 *  Safari (WebKit, ebenfalls Quartz unter macOS) zeigt die Fehler 1, 3 und 5
 *  genauso und bekommt dieselben Korrekturen (Punkt 2 vorsorglich). Dazu:
 *
 *  6. Safari wertet Media Queries beim Drucken gegen die verbreiterte
 *     Druckseite aus statt gegen den bedruckbaren Bereich; es greifen
 *     Desktop-Breakpoints (zweispaltig, größere Schrift, überbreite Tabellen).
 *     -> Jede Media Query wird wie in Chrome ausgewertet und festgeschrieben.
 *
 *  7. Safari druckt Scrollbalken überlaufender Bereiche mit, Chrome nicht.
 *     -> Scrollbalken werden im Druck-Dokument ausgeblendet.
 *
 *  8. WebKit ignoriert max-width bei Tabellen; Tabellen mit fester Breite
 *     laufen über und werden abgeschnitten.
 *     -> Überbreite Tabellen bekommen ihre max-width als Breite.
 *
 *  9. Safari teilt Bilder an Seitenumbrüchen, Chrome schiebt sie als Ganzes
 *     auf die nächste Seite. Ursache: WebKit paginiert Inhalte in Flex-
 *     Containern (Bootstrap-Rows) nicht, sondern schneidet sie ab.
 *     -> Flex-Zeilen, deren Spalten im Druck untereinander stehen, werden zu
 *        Blöcken (geometrisch identisch, nachgemessen); Bilder und Abbildungen
 *        erhalten zusätzlich break-inside: avoid.
 *
 * 10. WebKit verteilt die Tabellenbreite anders auf die Spalten als Chrome.
 *     -> Spaltenbreiten werden mit Chromes Algorithmus (CSS Tables 3)
 *        berechnet und fest gesetzt.
 *
 *  Den Druckmaßstab trifft Safari von sich aus (945 statt 944 px, < 0,1 %).
 *
 *  Alle Korrekturen laufen ausschließlich in Firefox bzw. Safari und nur im
 *  Druck-Iframe; in Chrome bleibt die Ausgabe unverändert.
 * ========================================================================== */

/**
 * Seitengeometrie des Drucks (A4 hoch). Wird für die @page-Regel und für die
 * Berechnung des Druckmaßstabs verwendet.
 */
const PRINT_PAGE = {
    widthMm: 210,
    heightMm: 297,
    marginTopMm: 12,
    marginRightMm: 15,
    marginBottomMm: 15,
    marginLeftMm: 15
};

/** Breite des bedruckbaren Seitenbereichs in CSS-px (96 dpi). */
const PRINT_PAGE_CONTENT_WIDTH_PX =
    ((PRINT_PAGE.widthMm - PRINT_PAGE.marginLeftMm - PRINT_PAGE.marginRightMm) * 96) / 25.4;

/** Höhe des bedruckbaren Seitenbereichs in CSS-px (96 dpi). */
const PRINT_PAGE_CONTENT_HEIGHT_PX =
    ((PRINT_PAGE.heightMm - PRINT_PAGE.marginTopMm - PRINT_PAGE.marginBottomMm) * 96) / 25.4;

const XHTML_NS = "http://www.w3.org/1999/xhtml";
const FX_ATTR = "data-print-fx";
const FX_STYLE_ATTR = "data-print-fx-styles";
const FX_PSEUDOS = [null, "::before", "::after"];

/**
 * Erkennt Firefox bzw. Gecko-basierte Browser (Firefox, LibreWolf, Waterfox …).
 * Firefox auf iOS (FxiOS) nutzt WebKit und ist bewusst ausgenommen.
 */
function isFirefoxBrowser() {
    if (typeof navigator === "undefined") return false;
    return /\bFirefox\//.test(navigator.userAgent || "");
}

/**
 * Wertet die Option firefoxFixes aus: true = immer, false = nie,
 * "auto" (Standard) = nur in Firefox.
 */
function shouldApplyFirefoxFixes(option = "auto") {
    if (option === true) return true;
    if (option === false) return false;
    return isFirefoxBrowser();
}

/**
 * Erkennt Safari bzw. die WebKit-Engine (Safari auf macOS, alle Browser auf
 * iOS/iPadOS). Chrome, Edge und Opera tragen „AppleWebKit“ ebenfalls im
 * User-Agent und sind daher ausdrücklich ausgenommen.
 */
function isSafariBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /AppleWebKit\//.test(ua) && !/(Chrome|Chromium|Edg|OPR|Firefox)\//.test(ua);
}

/**
 * Wertet die Option safariFixes aus: true = immer, false = nie,
 * "auto" (Standard) = nur in Safari/WebKit.
 */
function shouldApplySafariFixes(option = "auto") {
    if (option === true) return true;
    if (option === false) return false;
    return isSafariBrowser();
}

/**
 * Wartet, bis Webfonts und Bilder im Druck-Dokument geladen sind, damit
 * Messungen am endgültigen Layout erfolgen (mit Zeitlimit als Absicherung).
 */
async function waitForPrintResources(win, timeoutMs = 3000) {
    const doc = win.document;
    const pending = [];

    if (doc.fonts && doc.fonts.ready) {
        pending.push(doc.fonts.ready.catch(() => {}));
    }

    doc.querySelectorAll("img").forEach((img) => {
        if (img.complete) return;
        pending.push(
            new Promise((resolve) => {
                img.addEventListener("load", resolve, { once: true });
                img.addEventListener("error", resolve, { once: true });
            })
        );
    });

    if (!pending.length) return;

    await Promise.race([
        Promise.all(pending),
        new Promise((resolve) => setTimeout(resolve, timeoutMs))
    ]);
}

/**
 * Teilt eine kommagetrennte CSS-Liste (z. B. mehrere background-image-Ebenen)
 * nur an Kommas der obersten Ebene – Kommas in Klammern und Strings bleiben erhalten.
 */
function splitTopLevelList(value) {
    const result = [];
    let depth = 0;
    let quote = null;
    let current = "";

    for (const ch of String(value || "")) {
        if (quote) {
            current += ch;
            if (ch === quote) quote = null;
            continue;
        }

        if (ch === '"' || ch === "'") {
            quote = ch;
            current += ch;
            continue;
        }

        if (ch === "(") depth++;
        if (ch === ")") depth = Math.max(0, depth - 1);

        if (ch === "," && depth === 0) {
            result.push(current.trim());
            current = "";
            continue;
        }

        current += ch;
    }

    if (current.trim()) result.push(current.trim());
    return result;
}

/**
 * Bringt eine berechnete Ebenen-Liste (background-size, -position …) auf die
 * Anzahl der Hintergrund-Ebenen; kürzere Listen werden wie in CSS wiederholt.
 */
function expandLayerList(value, count, fallback) {
    const items = splitTopLevelList(value);
    if (!items.length) return Array(count).fill(fallback);
    return Array.from({ length: count }, (_, i) => items[i % items.length]);
}

const GRADIENT_LAYER_RE = /^(?:-(?:webkit|moz)-)?(?:repeating-)?(?:linear|radial|conic)-gradient\(/i;
const REPEATING_GRADIENT_RE = /^(?:-(?:webkit|moz)-)?repeating-/i;
const COLOR_FUNCTION_RE = /\b(rgba?|hsla?|hwb|lab|lch|oklab|oklch|color)\(([^()]*)\)/gi;
const COLOR_TOKEN_RE = /\b(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color)\([^()]*\)|#[0-9a-f]{3,8}\b|\btransparent\b/gi;

/**
 * Prüft, ob ein CSS-Ausdruck (z. B. ein Verlauf) mindestens eine Farbe mit
 * Transparenz enthält.
 */
function hasTranslucentColor(text) {
    if (/\btransparent\b/i.test(text)) return true;

    COLOR_FUNCTION_RE.lastIndex = 0;
    let match;

    while ((match = COLOR_FUNCTION_RE.exec(text))) {
        const args = match[2];
        let alpha = null;

        if (args.includes("/")) {
            alpha = args.split("/").pop().trim();
        } else {
            const parts = args.split(",").map((part) => part.trim());
            if (parts.length === 4) alpha = parts[3];
        }

        if (!alpha) continue;

        const number = parseFloat(alpha);
        if (Number.isNaN(number)) continue;

        const value = alpha.endsWith("%") ? number / 100 : number;
        if (value < 1) return true;
    }

    return false;
}

/**
 * Eine Hintergrundebene muss für Firefox gerastert werden, wenn sie ein
 * Verlauf mit Transparenz oder ein sich wiederholender Verlauf ist.
 */
function gradientLayerNeedsFix(layer) {
    if (!GRADIENT_LAYER_RE.test(layer)) return false;
    return REPEATING_GRADIENT_RE.test(layer) || hasTranslucentColor(layer);
}

/**
 * Wandelt eine beliebige CSS-Farbe über einen Canvas-Kontext in [r, g, b, a] um.
 */
function parseCssColor(value, win, ctx2d) {
    if (!value) return null;
    if (/^transparent$/i.test(value.trim())) return [0, 0, 0, 0];
    if (win.CSS && win.CSS.supports && !win.CSS.supports("color", value)) return null;

    ctx2d.fillStyle = "#000000";
    ctx2d.fillStyle = value;
    const normalized = String(ctx2d.fillStyle);

    const hex = normalized.match(/^#([0-9a-f]{6})$/i);
    if (hex) {
        const n = parseInt(hex[1], 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 1];
    }

    const rgba = normalized.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i);
    if (rgba) {
        return [Number(rgba[1]), Number(rgba[2]), Number(rgba[3]), rgba[4] === undefined ? 1 : Number(rgba[4])];
    }

    return null;
}

/**
 * Legt eine (ggf. transparente) Farbe über eine deckende Grundfarbe.
 */
function compositeOver(color, base) {
    const a = color[3];
    return [0, 1, 2].map((i) => color[i] * a + base[i] * (1 - a));
}

/**
 * Ermittelt näherungsweise die deckende Farbe hinter einem Element, indem die
 * Hintergrundfarben aller Vorfahren auf weißes Papier gelegt werden.
 */
function effectiveBackdropColor(el, pseudo, win, ctx2d) {
    const chain = [];

    for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
        chain.unshift(win.getComputedStyle(node).backgroundColor);
    }

    if (pseudo) chain.push(win.getComputedStyle(el, pseudo).backgroundColor);

    let base = [255, 255, 255];

    chain.forEach((value) => {
        const color = parseCssColor(value, win, ctx2d);
        if (color) base = compositeOver(color, base);
    });

    return base;
}

/**
 * Notlösung, falls Rastern nicht möglich ist: Alle Farbstopps des Verlaufs
 * werden vorab mit der Hintergrundfarbe verrechnet, sodass der Verlauf deckend
 * wird und Firefox ihn korrekt ausgibt.
 */
function flattenGradientColors(layer, backdrop, win, ctx2d) {
    let failed = false;

    const flattened = layer.replace(COLOR_TOKEN_RE, (token) => {
        const color = parseCssColor(token, win, ctx2d);
        if (!color) {
            failed = true;
            return token;
        }
        const [r, g, b] = compositeOver(color, backdrop).map((v) => Math.round(v));
        return `rgb(${r}, ${g}, ${b})`;
    });

    return failed ? "none" : flattened;
}

/**
 * Rastert eine einzelne Hintergrundebene über SVG-<foreignObject> und Canvas
 * in ein PNG (data-URL). Der Browser zeichnet den Verlauf dabei selbst, das
 * Ergebnis entspricht also exakt der Bildschirmdarstellung – inklusive Alpha.
 */
async function rasterizeBackgroundLayer(win, { width, height, css, scale }) {
    const doc = win.document;
    const maxPixels = 16e6;
    const maxSide = 8192;

    const effectiveScale = Math.min(
        scale,
        Math.sqrt(maxPixels / (width * height)),
        maxSide / width,
        maxSide / height
    );

    if (!(effectiveScale > 0.2)) {
        throw new Error("Hintergrundfläche ist zu groß zum Rastern");
    }

    const pxWidth = Math.max(1, Math.ceil(width * effectiveScale));
    const pxHeight = Math.max(1, Math.ceil(height * effectiveScale));

    const escapedCss = css
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${pxWidth}" height="${pxHeight}" ` +
        `viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">` +
        `<foreignObject x="0" y="0" width="${width}" height="${height}">` +
        `<div xmlns="${XHTML_NS}" style="${escapedCss}"></div>` +
        `</foreignObject></svg>`;

    const img = new win.Image();

    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("SVG-Rasterung fehlgeschlagen"));
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    });

    const canvas = doc.createElement("canvas");
    canvas.width = pxWidth;
    canvas.height = pxHeight;
    canvas.getContext("2d").drawImage(img, 0, 0, pxWidth, pxHeight);

    // Wirft bei einem "tainted" Canvas – dann greift die Notlösung
    return canvas.toDataURL("image/png");
}

/**
 * Liefert alle Elemente samt Pseudo-Elementen des Druck-Dokuments, die
 * untersucht werden sollen (SVG-Innenleben wird übersprungen).
 */
function collectStyleTargets(doc) {
    const targets = [];
    if (!doc.body) return targets;

    doc.body.querySelectorAll("*").forEach((el) => {
        const isHtml = el.namespaceURI === XHTML_NS;
        const parentIsHtml = el.parentElement && el.parentElement.namespaceURI === XHTML_NS;

        if (!isHtml && !parentIsHtml) return;

        (isHtml ? FX_PSEUDOS : [null]).forEach((pseudo) => targets.push({ el, pseudo }));
    });

    return targets;
}

/**
 * Pseudo-Elemente existieren nur, wenn sie Inhalt haben.
 */
function isRenderedBox(cs, pseudo) {
    if (cs.display === "none") return false;
    if (!pseudo) return true;
    return Boolean(cs.content) && cs.content !== "none" && cs.content !== "normal";
}

/**
 * Hilfsobjekt, das Stil-Überschreibungen für Elemente (inline, !important)
 * und Pseudo-Elemente (per generierter CSS-Regel) sammelt.
 */
function createStyleWriter(doc) {
    const rules = [];

    // Zähler je Dokument, damit mehrere Writer keine IDs doppelt vergeben
    const idFor = (el) => {
        let id = el.getAttribute(FX_ATTR);
        if (!id) {
            doc.__printFxCounter = (doc.__printFxCounter || 0) + 1;
            id = String(doc.__printFxCounter);
            el.setAttribute(FX_ATTR, id);
        }
        return id;
    };

    return {
        rules,
        set(el, pseudo, declarations) {
            if (!pseudo) {
                declarations.forEach(([property, value]) => {
                    el.style.setProperty(property, value, "important");
                });
                return;
            }

            // Dreifacher Attribut-Selektor für hohe Spezifität
            const selector = `[${FX_ATTR}="${idFor(el)}"]`.repeat(3) + pseudo;
            const body = declarations.map(([property, value]) => `${property}: ${value} !important;`).join(" ");
            rules.push(`${selector} { ${body} }`);
        },
        flush() {
            if (!rules.length) return;
            const style = doc.createElement("style");
            style.setAttribute(FX_STYLE_ATTR, "");
            style.textContent = rules.join("\n");
            (doc.head || doc.documentElement).appendChild(style);
        }
    };
}

/**
 * Prüft, ob ein Element Containing Block für absolut positionierte
 * Nachfahren ist.
 */
function establishesAbsoluteContainingBlock(cs) {
    const notNone = (value) => Boolean(value) && value !== "none";

    if (cs.position && cs.position !== "static") return true;
    if (notNone(cs.transform) || notNone(cs.translate) || notNone(cs.rotate) || notNone(cs.scale)) return true;
    if (notNone(cs.perspective) || notNone(cs.filter) || notNone(cs.backdropFilter)) return true;
    if (cs.contain && /\b(?:layout|paint|strict|content)\b/.test(cs.contain)) return true;
    if (cs.containerType && /size/.test(cs.containerType)) return true;
    if (cs.contentVisibility === "auto" || cs.contentVisibility === "hidden") return true;
    if (cs.willChange && /\b(?:transform|translate|rotate|scale|perspective|filter|position|contain)\b/.test(cs.willChange)) return true;

    return false;
}

/**
 * Elemente, die sich als neuer Containing Block eignen (keine Inline-Boxen,
 * keine display:contents-Elemente).
 */
function canBecomeContainingBlock(cs) {
    const display = cs.display || "";
    if (display === "inline" || display === "contents" || display === "none") return false;
    if (display.startsWith("table-column") || display.startsWith("ruby")) return false;
    return true;
}

/**
 * Ursprung der Padding-Box eines Containing Blocks in Viewport-Koordinaten
 * (null = initialer Containing Block).
 */
function paddingBoxOrigin(node, win) {
    if (!node) {
        return { x: -(win.scrollX || 0), y: -(win.scrollY || 0) };
    }

    const rect = node.getBoundingClientRect();
    const isRoot = node === node.ownerDocument.documentElement || node === node.ownerDocument.body;

    return {
        x: rect.left + node.clientLeft - (isRoot ? 0 : node.scrollLeft),
        y: rect.top + node.clientTop - (isRoot ? 0 : node.scrollTop)
    };
}

/**
 * Prüft, ob ein Element seinen Inhalt beschneidet (overflow ≠ visible). Würde
 * ein solches Element neuer Containing Block, würden bisher herausragende
 * absolut positionierte Elemente plötzlich abgeschnitten.
 */
function clipsOverflow(cs) {
    return cs.overflowX !== "visible" || cs.overflowY !== "visible";
}

/**
 * Korrektur 2: Absolut positionierte Elemente an einen nahen Containing Block
 * binden, damit Firefox sie nach Seitenumbrüchen nicht verschiebt.
 *
 * Achsen mit statischer Position (left/right bzw. top/bottom = auto) hängen
 * nicht vom Containing Block ab und bleiben unverändert; alle anderen Werte
 * werden aus dem aktuellen Layout gemessen und relativ zum neuen Containing
 * Block fest gesetzt. Optisch ändert sich dadurch nichts.
 */
function pinAbsoluteBoxesToLocalContainingBlock(win, writer) {
    const doc = win.document;
    const boxes = [];
    const px = (value) => (/px$/.test(value) ? parseFloat(value) : NaN);

    // Phase 1: Alles messen, bevor irgendetwas verändert wird
    collectStyleTargets(doc).forEach(({ el, pseudo }) => {
        const cs = win.getComputedStyle(el, pseudo);
        if (cs.position !== "absolute" || !isRenderedBox(cs, pseudo)) return;

        const start = pseudo ? el : el.parentElement;
        if (!start) return;

        let oldCB = null;
        let pathClips = false;

        for (let node = start; node && node.nodeType === 1; node = node.parentElement) {
            const nodeCs = win.getComputedStyle(node);
            if (establishesAbsoluteContainingBlock(nodeCs)) {
                oldCB = node;
                break;
            }
            if (clipsOverflow(nodeCs)) pathClips = true;
        }

        const horizontalStatic = cs.left === "auto" && cs.right === "auto";
        const verticalStatic = cs.top === "auto" && cs.bottom === "auto";

        const box = {
            el,
            pseudo,
            start,
            oldCB,
            horizontalStatic,
            verticalStatic,
            pinnable: !pathClips,
            origin: { x: 0, y: 0 },
            sizes: [
                ["width", px(cs.width)],
                ["height", px(cs.height)],
                ["margin-top", px(cs.marginTop)],
                ["margin-right", px(cs.marginRight)],
                ["margin-bottom", px(cs.marginBottom)],
                ["margin-left", px(cs.marginLeft)]
            ].filter(([, value]) => Number.isFinite(value))
        };

        if (box.pinnable && (!horizontalStatic || !verticalStatic)) {
            const left = px(cs.left);
            const top = px(cs.top);
            const oldCBIsBlock = !oldCB || canBecomeContainingBlock(win.getComputedStyle(oldCB));

            if (
                oldCBIsBlock &&
                (horizontalStatic || Number.isFinite(left)) &&
                (verticalStatic || Number.isFinite(top))
            ) {
                const cbOrigin = paddingBoxOrigin(oldCB, win);
                box.origin = { x: cbOrigin.x + (left || 0), y: cbOrigin.y + (top || 0) };
            } else if (!pseudo && cs.transform === "none") {
                const rect = el.getBoundingClientRect();
                box.origin = {
                    x: rect.left - (parseFloat(cs.marginLeft) || 0),
                    y: rect.top - (parseFloat(cs.marginTop) || 0)
                };
            } else {
                box.pinnable = false;
            }
        }

        boxes.push(box);
    });

    if (!boxes.length) return;

    // Elemente auf dem Weg nicht fixierbarer Boxen dürfen nicht positioniert
    // werden, sonst würde sich deren Containing Block unkontrolliert ändern
    const forbidden = new Set();
    boxes.forEach((box) => {
        if (box.pinnable) return;
        for (let node = box.start; node && node !== box.oldCB; node = node.parentElement) {
            forbidden.add(node);
        }
    });

    // Phase 2: Für jede Box den nächstgelegenen geeigneten Vorfahren wählen
    const anchors = new Map();

    boxes.forEach((box) => {
        if (!box.pinnable) return;

        for (let node = box.start; node && node !== box.oldCB; node = node.parentElement) {
            if (node === doc.body || node === doc.documentElement) break;
            if (anchors.has(node)) break;
            if (forbidden.has(node)) continue;

            const nodeCs = win.getComputedStyle(node);
            if (!canBecomeContainingBlock(nodeCs)) continue;

            const parentCs = node.parentElement ? win.getComputedStyle(node.parentElement) : null;
            const isFlexOrGridItem = Boolean(parentCs && /flex|grid/.test(parentCs.display));

            anchors.set(node, {
                origin: paddingBoxOrigin(node, win),
                resetZIndex: nodeCs.zIndex !== "auto" && !isFlexOrGridItem
            });
            break;
        }
    });

    if (!anchors.size) return;

    // Phase 3: Neue Containing Blocks setzen und Boxen mit gemessenen Werten fixieren
    anchors.forEach((info, node) => {
        const declarations = [
            ["position", "relative"],
            ["top", "auto"],
            ["right", "auto"],
            ["bottom", "auto"],
            ["left", "auto"]
        ];

        if (info.resetZIndex) declarations.push(["z-index", "auto"]);

        writer.set(node, null, declarations);
    });

    const round = (value) => `${Math.round(value * 1000) / 1000}px`;

    boxes.forEach((box) => {
        if (!box.pinnable) return;

        let newCB = null;
        for (let node = box.start; node && node.nodeType === 1; node = node.parentElement) {
            if (anchors.has(node) || node === box.oldCB) {
                newCB = node;
                break;
            }
        }

        if (newCB === box.oldCB) return;

        const cbOrigin = anchors.get(newCB).origin;
        const declarations = box.sizes.map(([property, value]) => [property, round(value)]);

        if (!box.horizontalStatic) {
            declarations.push(["left", round(box.origin.x - cbOrigin.x)], ["right", "auto"]);
        }

        if (!box.verticalStatic) {
            declarations.push(["top", round(box.origin.y - cbOrigin.y)], ["bottom", "auto"]);
        }

        writer.set(box.el, box.pseudo, declarations);
    });
}

/**
 * Korrektur 1: Verlaufsebenen mit Transparenz in PNGs rastern (Notlösung:
 * Farben mit dem Hintergrund verrechnen), damit Firefox sie nicht schwarz druckt.
 */
async function rasterizeTranslucentGradients(win, writer, rasterScale) {
    const doc = win.document;
    const colorCanvas = doc.createElement("canvas").getContext("2d");
    const jobs = [];
    const dataUrls = [];

    // Phase 1: Alle betroffenen Ebenen erfassen (nur lesen)
    collectStyleTargets(doc).forEach(({ el, pseudo }) => {
        const cs = win.getComputedStyle(el, pseudo);
        if (!isRenderedBox(cs, pseudo)) return;

        const backgroundImage = cs.backgroundImage;
        if (!backgroundImage || backgroundImage === "none" || !/gradient\(/i.test(backgroundImage)) return;

        const layers = splitTopLevelList(backgroundImage);
        const count = layers.length;

        const clips = expandLayerList(cs.backgroundClip, count, "border-box");
        const fixIndexes = layers
            .map((layer, i) => (gradientLayerNeedsFix(layer) && clips[i] !== "text" ? i : -1))
            .filter((i) => i >= 0);

        if (!fixIndexes.length) return;

        const px = (value) => parseFloat(value) || 0;
        const borders = [cs.borderTopWidth, cs.borderRightWidth, cs.borderBottomWidth, cs.borderLeftWidth].map(px);
        const paddings = [cs.paddingTop, cs.paddingRight, cs.paddingBottom, cs.paddingLeft].map(px);

        let width = parseFloat(cs.width);
        let height = parseFloat(cs.height);

        if (cs.boxSizing !== "border-box") {
            width += paddings[1] + paddings[3] + borders[1] + borders[3];
            height += paddings[0] + paddings[2] + borders[0] + borders[2];
        }

        const rasterizable =
            cs.display !== "inline" && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;

        jobs.push({
            el,
            pseudo,
            layers,
            fixIndexes,
            rasterizable,
            width,
            height,
            borders,
            paddings,
            sizes: expandLayerList(cs.backgroundSize, count, "auto"),
            positions: expandLayerList(cs.backgroundPosition, count, "0% 0%"),
            repeats: expandLayerList(cs.backgroundRepeat, count, "repeat"),
            origins: expandLayerList(cs.backgroundOrigin, count, "padding-box"),
            clips,
            attachments: expandLayerList(cs.backgroundAttachment, count, "scroll"),
            backdrop: effectiveBackdropColor(el, pseudo, win, colorCanvas)
        });
    });

    // Phase 2: Rastern und ersetzen
    for (const job of jobs) {
        const images = [...job.layers];
        const sizes = [...job.sizes];
        const positions = [...job.positions];
        const repeats = [...job.repeats];
        const origins = [...job.origins];
        const attachments = [...job.attachments];

        for (const i of job.fixIndexes) {
            let replaced = false;

            if (job.rasterizable) {
                try {
                    const css = [
                        "display:block",
                        "margin:0",
                        "box-sizing:border-box",
                        `width:${job.width}px`,
                        `height:${job.height}px`,
                        "border-style:solid",
                        "border-color:transparent",
                        `border-width:${job.borders.join("px ")}px`,
                        `padding:${job.paddings.join("px ")}px`,
                        "background-color:transparent",
                        `background-image:${job.layers[i]}`,
                        `background-size:${job.sizes[i]}`,
                        `background-position:${job.positions[i]}`,
                        `background-repeat:${job.repeats[i]}`,
                        `background-origin:${job.origins[i]}`,
                        `background-clip:${job.clips[i]}`,
                        "background-attachment:scroll"
                    ].join(";");

                    const dataUrl = await rasterizeBackgroundLayer(win, {
                        width: job.width,
                        height: job.height,
                        css,
                        scale: rasterScale
                    });

                    dataUrls.push(dataUrl);
                    images[i] = `url("${dataUrl}")`;
                    sizes[i] = "100% 100%";
                    positions[i] = "0% 0%";
                    repeats[i] = "no-repeat";
                    origins[i] = "border-box";
                    attachments[i] = "scroll";
                    replaced = true;
                } catch (err) {
                    console.warn("Firefox-Druckkorrektur: Verlauf konnte nicht gerastert werden.", err);
                }
            }

            if (!replaced) {
                images[i] = flattenGradientColors(job.layers[i], job.backdrop, win, colorCanvas);
            }
        }

        writer.set(job.el, job.pseudo, [
            ["background-image", images.join(", ")],
            ["background-size", sizes.join(", ")],
            ["background-position", positions.join(", ")],
            ["background-repeat", repeats.join(", ")],
            ["background-origin", origins.join(", ")],
            ["background-attachment", attachments.join(", ")]
        ]);
    }

    return dataUrls;
}

/**
 * Ermittelt die Grundlinie der ersten Textzeile eines Elements (Viewport-y),
 * über ein unsichtbares Mess-Element ohne Breite und Höhe.
 */
function firstLineBaseline(el) {
    const doc = el.ownerDocument;
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP)
    });
    const text = walker.nextNode();
    if (!text || !text.parentNode) return null;

    const probe = doc.createElement("span");
    probe.style.cssText =
        "display:inline-block;width:0;height:0;margin:0;padding:0;border:0;vertical-align:baseline;";
    text.parentNode.insertBefore(probe, text);
    const baseline = probe.getBoundingClientRect().top;
    probe.remove();

    return baseline;
}

/**
 * Prüft, ob der erste Inhalt eines Elements inline ist (Text oder Inline-
 * Element) – dann sitzt ein ::before auf der ersten Textzeile.
 */
function startsWithInlineContent(el, win) {
    for (const node of el.childNodes) {
        if (node.nodeType === 3) {
            if (node.nodeValue.trim()) return true;
            continue;
        }
        if (node.nodeType !== 1) continue;
        const display = win.getComputedStyle(node).display;
        if (display === "none" || display === "contents") continue;
        return display.startsWith("inline") || display === "ruby";
    }
    return false;
}

/**
 * Korrektur 5 (Firefox): Aufzählungspunkte wie Chrome.
 *
 * Firefox setzt „disc“-Punkte als Zeichen einer eigenen Bullet-Schrift. Deren
 * Metrik vergrößert die erste Zeile jedes Listenpunkts (~0,6 px), zudem ist
 * der Punkt größer und sitzt weiter rechts als in Chrome. Chrome zeichnet
 * stattdessen einen Kreis, dessen Maße aus dem Schrift-Ascent A (ganze px)
 * folgen: d = ⌊A·2/3⌋, Durchmesser ⌊(d+1)/2⌋, linke Kante d+7 px vor dem
 * Text, Oberkante ⌊3·(A−d)/2⌋ px unter der Ascent-Linie.
 *
 * Der Firefox-Punkt wird entfernt und durch genau diesen Kreis ersetzt – als
 * an der Grundlinie ausgerichtetes Inline-Element ohne Breite (verändert
 * weder Zeilenhöhe noch Textposition). Beginnt der Listenpunkt mit einem
 * Block-Element, wird der Kreis stattdessen als Float positioniert.
 */
function replaceDiscMarkersLikeChrome(win) {
    const doc = win.document;
    const writer = createStyleWriter(doc);
    const ctx = doc.createElement("canvas").getContext("2d");

    const items = Array.from(doc.querySelectorAll("li")).filter((li) => {
        const cs = win.getComputedStyle(li);
        if (cs.display !== "list-item") return false;
        if (cs.listStyleType !== "disc" || cs.listStylePosition !== "outside") return false;
        if (cs.listStyleImage && cs.listStyleImage !== "none") return false;
        // ::before muss frei sein
        return !isRenderedBox(win.getComputedStyle(li, "::before"), "::before");
    });

    if (!items.length) return 0;

    // Farben vorab lesen, dann alle Firefox-Punkte auf einmal entfernen
    const colors = items.map((li) => {
        const marker = win.getComputedStyle(li, "::marker");
        return (marker && marker.color) || win.getComputedStyle(li).color;
    });
    items.forEach((li) => li.style.setProperty("list-style-type", "none", "important"));

    const round = (value) => `${Math.round(value * 1000) / 1000}px`;

    // Oberlänge (Ascent) als Anteil der Schriftgröße. Gemessen bei 1000 px, weil
    // Firefox Schriftmetriken bei kleinen Größen auf ganze Pixel aufrundet
    // (z. B. 13,36 → 14 px), Chrome aber mit dem exakten Wert rechnet und erst
    // dann rundet. Bei 1000 px fällt die Rundung nicht ins Gewicht.
    const ascentRatios = new Map();
    const ascentRatio = (cs) => {
        const key = `${cs.fontStyle}|${cs.fontWeight}|${cs.fontFamily}`;
        if (!ascentRatios.has(key)) {
            ctx.font = `${cs.fontStyle} ${cs.fontWeight} 1000px ${cs.fontFamily}`;
            const ascent = ctx.measureText("x").fontBoundingBoxAscent;
            ascentRatios.set(key, ascent > 500 && ascent < 1500 ? ascent / 1000 : 0.93);
        }
        return ascentRatios.get(key);
    };

    items.forEach((li, i) => {
        const cs = win.getComputedStyle(li);

        const fontSize = parseFloat(cs.fontSize) || 16;
        const ascent = ascentRatio(cs) * fontSize;

        // Chrome-Geometrie (ganze Pixel)
        const a = Math.round(ascent);
        const offset = Math.floor((a * 2) / 3);
        const size = Math.max(1, Math.floor((offset + 1) / 2));
        const topBelowAscent = Math.floor((3 * (a - offset)) / 2);
        const left = -(offset + 7);

        const common = [
            ["content", '""'],
            ["box-sizing", "content-box"],
            ["width", `${size}px`],
            ["height", `${size}px`],
            ["padding", "0"],
            ["border", "0"],
            ["border-radius", "50%"],
            // Fläche statt Rahmen: Rahmen werden beim Drucken auf Gerätepixel
            // gerundet. print-color-adjust: Punkt auch ohne „Hintergründe drucken“.
            ["background", colors[i]],
            ["print-color-adjust", "exact"],
            ["-webkit-print-color-adjust", "exact"],
            ["position", "static"],
            ["float", "none"]
        ];

        if (startsWithInlineContent(li, win)) {
            // Unterkante liegt (a − top − size) px über der Grundlinie;
            // Ränder heben die Breite auf, der Text bleibt an seiner Stelle
            writer.set(li, "::before", [
                ...common,
                ["display", "inline-block"],
                ["vertical-align", `${a - topBelowAscent - size}px`],
                ["margin", `0 ${-(left + size)}px 0 ${left}px`],
                ["line-height", "0"]
            ]);
            return;
        }

        // Fallback: Listenpunkt beginnt mit Block-Element → Float an der
        // gemessenen Grundlinie der ersten Zeile
        const baseline = firstLineBaseline(li);
        if (baseline == null) return;

        const rect = li.getBoundingClientRect();
        const contentTop = rect.top + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0);
        const top = baseline - a + topBelowAscent;

        writer.set(li, "::before", [
            ...common,
            ["display", "block"],
            ["float", "left"],
            ["margin", `${round(top - contentTop)} 0 0 ${left}px`]
        ]);
    });

    writer.flush();
    return items.length;
}

/**
 * Safari-Korrektur: max-width bei Tabellen wie Chrome anwenden.
 *
 * WebKit ignoriert max-width bei Tabellen. Hat eine Tabelle eine feste Breite
 * (z. B. aus dem Redaktionssystem) und max-width: 100 %, begrenzt Chrome sie
 * auf die Containerbreite, Safari nicht – die Tabelle läuft über und wird im
 * scrollbaren Wrapper abgeschnitten. Betroffene Tabellen bekommen deshalb ihre
 * max-width als Breite.
 */
function capTablesToMaxWidth(win) {
    const doc = win.document;
    const writer = createStyleWriter(doc);
    let count = 0;

    doc.querySelectorAll("table").forEach((table) => {
        const cs = win.getComputedStyle(table);
        const maxWidth = cs.maxWidth;
        if (!maxWidth || maxWidth === "none") return;

        let maxPx;
        if (maxWidth.endsWith("%")) {
            const parent = table.parentElement;
            if (!parent) return;
            const pcs = win.getComputedStyle(parent);
            const contentWidth =
                parent.clientWidth - (parseFloat(pcs.paddingLeft) || 0) - (parseFloat(pcs.paddingRight) || 0);
            maxPx = (contentWidth * parseFloat(maxWidth)) / 100;
        } else if (maxWidth.endsWith("px")) {
            maxPx = parseFloat(maxWidth);
        } else {
            return;
        }

        if (!(table.getBoundingClientRect().width > maxPx + 0.5)) return;

        writer.set(table, null, [["width", maxWidth]]);
        count++;
    });

    writer.flush();
    return count;
}

/**
 * Safari-Korrektur: Flex-Zeilen, deren Elemente untereinander stehen, als
 * normale Blöcke setzen.
 *
 * WebKit paginiert Inhalte in Flex-Containern nicht: Was über das Seitenende
 * ragt, wird abgeschnitten (z. B. ein Bild in einer Bootstrap-Spalte), und
 * break-inside wird ignoriert. Im Druck stehen die Spalten ohnehin unter-
 * einander; als Block-Layout ist die Geometrie identisch, aber WebKit bricht
 * sauber um. Jede Umstellung wird nachgemessen und bei Abweichung verworfen.
 */
function unflexStackedRows(win) {
    const doc = win.document;
    const px = (value) => parseFloat(value) || 0;
    const candidates = [];

    doc.querySelectorAll("body *").forEach((el) => {
        const cs = win.getComputedStyle(el);
        if (cs.display !== "flex" && cs.display !== "-webkit-flex") return;
        if (cs.flexDirection !== "row") return;
        if (px(cs.rowGap) > 0 || px(cs.columnGap) > 0) return;

        const kids = Array.from(el.children).filter((kid) => {
            const kcs = win.getComputedStyle(kid);
            return kcs.display !== "none" && kcs.position !== "absolute" && kcs.position !== "fixed";
        });
        if (!kids.length) return;

        const contentWidth = el.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight);
        const kidStyles = kids.map((kid) => win.getComputedStyle(kid));

        // Jedes Element muss die volle Breite einnehmen (also untereinander stehen)
        const stacked = kids.every((kid, i) => {
            const kcs = kidStyles[i];
            const outer = kid.getBoundingClientRect().width + px(kcs.marginLeft) + px(kcs.marginRight);
            return Math.abs(outer - contentWidth) < 1 && (kcs.order === "0" || kcs.order === "");
        });
        if (!stacked) return;

        // Im Block-Layout würden aufeinandertreffende Außenabstände zusammenfallen
        for (let i = 1; i < kids.length; i++) {
            const above = px(kidStyles[i - 1].marginBottom);
            const below = px(kidStyles[i].marginTop);
            if (above < 0 || below < 0 || (above > 0 && below > 0)) return;
        }

        candidates.push({
            el,
            kids,
            displays: kidStyles.map((kcs) => kcs.display),
            rects: kids.map((kid) => kid.getBoundingClientRect())
        });
    });

    let count = 0;

    candidates.forEach(({ el, kids, displays, rects }) => {
        const previous = [el, ...kids].map((node) => node.getAttribute("style"));

        el.style.setProperty("display", "flow-root", "important");
        kids.forEach((kid, i) => kid.style.setProperty("display", displays[i], "important"));

        // Nachmessen: Lage und Größe müssen exakt erhalten bleiben
        const unchanged = kids.every((kid, i) => {
            const now = kid.getBoundingClientRect();
            return (
                Math.abs(now.left - rects[i].left) < 0.5 &&
                Math.abs(now.top - rects[i].top) < 0.5 &&
                Math.abs(now.width - rects[i].width) < 0.5 &&
                Math.abs(now.height - rects[i].height) < 0.5
            );
        });

        if (unchanged) {
            count++;
            return;
        }

        [el, ...kids].forEach((node, i) => {
            if (previous[i] === null) node.removeAttribute("style");
            else node.setAttribute("style", previous[i]);
        });
    });

    return count;
}

/**
 * Misst Mindest- und Wunschbreite (min-/max-content) des Inhalts einer
 * Tabellenzelle über einen unsichtbaren Klon, der die Schriftstile der Zelle erbt.
 */
function measureCellContent(cell) {
    const doc = cell.ownerDocument;
    const probe = doc.createElement("div");
    probe.style.cssText =
        "position:absolute;left:0;top:0;visibility:hidden;margin:0;padding:0;border:0;width:min-content;";
    Array.from(cell.childNodes).forEach((node) => probe.appendChild(node.cloneNode(true)));
    cell.appendChild(probe);

    const min = probe.getBoundingClientRect().width;
    probe.style.width = "max-content";
    const max = probe.getBoundingClientRect().width;

    probe.remove();
    return { min, max: Math.max(min, max) };
}

/**
 * Verteilt die Tabellenbreite wie Chrome (CSS Tables 3, „distributing width
 * to columns“): Zwischen den Breiten-Schätzungen min-content, min-content mit
 * Prozentspalten, min-content mit festen Spalten und max-content wird linear
 * interpoliert; Überschuss geht anteilig an die automatischen Spalten.
 */
function distributeTableWidthLikeChrome(cols, width) {
    const sum = (list) => list.reduce((total, value) => total + value, 0);
    const pct = (col) => Math.max(col.min, (col.pct / 100) * width);

    const guesses = [
        cols.map((col) => col.min),
        cols.map((col) => (col.pct ? pct(col) : col.min)),
        cols.map((col) => (col.pct ? pct(col) : col.fixed ? col.max : col.min)),
        cols.map((col) => (col.pct ? pct(col) : col.max))
    ];

    if (width <= sum(guesses[0])) return guesses[0];

    for (let k = 0; k < guesses.length - 1; k++) {
        const low = guesses[k];
        const high = guesses[k + 1];
        const lowSum = sum(low);
        const highSum = sum(high);

        if (width <= highSum) {
            if (highSum - lowSum < 1e-6) return high;
            const t = (width - lowSum) / (highSum - lowSum);
            return low.map((value, i) => value + (high[i] - value) * t);
        }
    }

    // Breiter als max-content: Überschuss verteilen
    const max = guesses[3];
    const excess = width - sum(max);
    const pick = (filter) => cols.map((col, i) => (filter(col) ? i : -1)).filter((i) => i >= 0);

    const auto = pick((col) => !col.pct && !col.fixed);
    const fixed = pick((col) => col.fixed && !col.pct);
    const percent = pick((col) => col.pct);

    let weights = cols.map(() => 0);
    const byMax = (indexes) => {
        const total = sum(indexes.map((i) => max[i]));
        indexes.forEach((i) => (weights[i] = total > 0 ? max[i] / total : 1 / indexes.length));
    };

    if (auto.length) byMax(auto);
    else if (fixed.length) byMax(fixed);
    else if (percent.length) {
        const total = sum(percent.map((i) => cols[i].pct));
        percent.forEach((i) => (weights[i] = cols[i].pct / total));
    } else weights = cols.map(() => 1 / cols.length);

    return max.map((value, i) => value + excess * weights[i]);
}

/**
 * Safari-Korrektur: Spaltenbreiten von Tabellen wie Chrome.
 *
 * WebKit verteilt die verfügbare Breite nach einem älteren Verfahren auf die
 * Spalten als Chrome; Zellen umbrechen dadurch anders und Zeilen werden höher.
 * Für einfache Tabellen (ohne verbundene Zellen) werden die Spaltenbreiten mit
 * Chromes Algorithmus berechnet und als feste Breiten gesetzt.
 */
function layoutTablesLikeChrome(win) {
    const doc = win.document;
    const px = (value) => parseFloat(value) || 0;
    const plans = [];

    doc.querySelectorAll("table").forEach((table) => {
        const tcs = win.getComputedStyle(table);
        if (tcs.tableLayout === "fixed") return;
        if (table.querySelector("col[width], col[style*='width']")) return;

        const rows = Array.from(table.rows);
        if (!rows.length) return;

        const grid = rows.map((row) => Array.from(row.cells));
        const columns = grid[0].length;
        if (!columns) return;
        if (grid.some((cells) => cells.length !== columns || cells.some((c) => c.colSpan !== 1 || c.rowSpan !== 1))) {
            return;
        }

        const collapse = tcs.borderCollapse === "collapse";
        const cols = Array.from({ length: columns }, () => ({ min: 0, max: 0, pct: 0, fixed: 0 }));

        grid.forEach((cells) =>
            cells.forEach((cell, i) => {
                const ccs = win.getComputedStyle(cell);
                const borders = px(ccs.borderLeftWidth) + px(ccs.borderRightWidth);
                const extra = px(ccs.paddingLeft) + px(ccs.paddingRight) + (collapse ? borders / 2 : borders);
                const content = measureCellContent(cell);

                cols[i].min = Math.max(cols[i].min, content.min + extra);
                cols[i].max = Math.max(cols[i].max, content.max + extra);

                // Angegebene Breite (Inline-Stil oder width-Attribut, z. B. aus dem Redaktionssystem)
                const spec = (cell.style.width || cell.getAttribute("width") || "").trim();
                if (/%$/.test(spec)) {
                    cols[i].pct = Math.max(cols[i].pct, parseFloat(spec));
                } else if (/^[\d.]+(px)?$/.test(spec)) {
                    const value = parseFloat(spec) + (ccs.boxSizing === "border-box" ? 0 : extra);
                    cols[i].fixed = Math.max(cols[i].fixed, value);
                }
            })
        );

        cols.forEach((col) => {
            if (col.fixed && !col.pct) col.max = Math.max(col.min, col.fixed);
        });

        // Verfügbare Breite: Summe der aktuellen Zellbreiten der ersten Zeile
        const firstRow = grid[0];
        const width = firstRow.reduce((total, cell) => total + cell.getBoundingClientRect().width, 0);
        const widths = distributeTableWidthLikeChrome(cols, width);

        plans.push({ table, firstRow, widths });
    });

    const writer = createStyleWriter(doc);
    plans.forEach(({ table, firstRow, widths }) => {
        writer.set(table, null, [["table-layout", "fixed"]]);
        firstRow.forEach((cell, i) => {
            writer.set(cell, null, [
                ["box-sizing", "border-box"],
                ["width", `${Math.round(widths[i] * 1000) / 1000}px`]
            ]);
        });
    });
    writer.flush();

    return plans.length;
}

/**
 * Safari-Korrektur: Bilder nicht über Seitenumbrüche teilen.
 * Chrome und Firefox schieben ein Bild, das nicht mehr auf die Seite passt,
 * vollständig auf die nächste Seite; Safari zerschneidet es.
 */
const SAFARI_PRINT_CSS = [
    "html body img, html body svg, html body video, html body canvas, html body picture, html body figure {",
    "  break-inside: avoid !important; page-break-inside: avoid !important;",
    "}",
    // Chrome zeichnet beim Drucken keine Scrollbalken, Safari schon
    "html body * { scrollbar-width: none !important; }",
    "html body *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }"
].join("\n");

/**
 * Wendet alle Firefox-Korrekturen auf das (bereits geladene) Druck-Dokument an.
 * Wird von printSelectorToPrinter automatisch in Firefox aufgerufen.
 */
export async function applyFirefoxPrintFixes(
    win,
    {
        fixGradients = true,
        fixAbsolutePositions = true,
        disableLigatures = true,
        fixListBullets = true,
        rasterScale = 2
    } = {}
) {
    const doc = win.document;

    // Aufzählungspunkte zuerst ersetzen – das ändert Zeilenhöhen, alle
    // folgenden Messungen sehen dann das endgültige Layout
    if (fixListBullets) {
        replaceDiscMarkersLikeChrome(win);
    }

    const writer = createStyleWriter(doc);

    // Erst messen/positionieren (benötigt das unveränderte Layout) …
    if (fixAbsolutePositions) {
        pinAbsoluteBoxesToLocalContainingBlock(win, writer);
    }

    // … dann Hintergründe ersetzen (ändert das Layout nicht)
    const rasterUrls = fixGradients ? await rasterizeTranslucentGradients(win, writer, rasterScale) : [];

    if (disableLigatures) {
        writer.rules.push(
            "html body *, html body *::before, html body *::after { font-variant-ligatures: no-common-ligatures !important; }"
        );
    }

    writer.flush();

    // Gerasterte Hintergründe vorab dekodieren, damit sie beim Klonen für die
    // Druckvorschau sofort verfügbar sind
    if (rasterUrls.length) {
        await Promise.all(
            rasterUrls.map((url) => {
                const img = new win.Image();
                img.src = url;
                return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
            })
        );
        await new Promise((resolve) => setTimeout(resolve, 50));
    }
}

/* ========================================================================== *
 *  Diagnose der Druckbreite (nur zur Fehlersuche)
 *
 *  Aktivieren ohne Codeänderung – in der Browser-Konsole der Seite:
 *      localStorage.printLayoutDebug = "1"
 *  danach ganz normal drucken (Button bzw. Strg+P), zum Abschalten:
 *      localStorage.removeItem("printLayoutDebug")
 *  Alternativ per Option printLayoutDebug: true.
 *
 *  Im Debug-Modus passiert zweierlei:
 *  1. „[print-debug]“: Das Druck-Dokument wird mit den echten Optionen und
 *     aktivierten @media-print-Regeln in Seitenbreite nachgebaut und vermessen.
 *  2. „[print-debug-map]“ + farbige Haarlinien im PDF: Jedes Element bekommt
 *     eine 1-px-Outline in einer eindeutigen Farbe. Outlines verändern weder
 *     Layout noch Druckbreite; aus dem PDF lässt sich so die echte Druck-
 *     geometrie jedes Elements ablesen. Die Map ordnet Farbe → Element zu.
 * ========================================================================== */

/**
 * Prüft, ob die Druck-Diagnose aktiv ist (Option, localStorage oder globale Variable).
 */
function isPrintLayoutDebugEnabled(options = {}) {
    if (options.printLayoutDebug) return true;
    try {
        if (window.localStorage && window.localStorage.getItem("printLayoutDebug") === "1") return true;
    } catch (err) {
        /* localStorage nicht verfügbar */
    }
    return window.PRINT_LAYOUT_DEBUG === true;
}

/**
 * Wandelt eine Media-Query-Liste so um, wie sie beim Drucken greifen würde:
 * print-Regeln werden aktiv, screen-Regeln inaktiv, Breitenbedingungen bleiben.
 */
function mediaTextAsPrint(text) {
    const result = [];

    String(text || "")
        .split(",")
        .forEach((raw) => {
            const query = raw.trim();
            const lower = query.toLowerCase();

            if (!query) return;
            if (/^not\s+print\b/.test(lower)) return;
            if (/^not\s+(screen|speech)\b/.test(lower)) return result.push("all");
            if (/^(only\s+)?print\b/.test(lower)) return result.push(query.replace(/^(only\s+)?print/i, "all"));
            if (/^(only\s+)?(screen|speech)\b/.test(lower)) return;

            result.push(query);
        });

    return result.length ? result.join(", ") : "not all";
}

/**
 * Aktiviert in einem Dokument per CSSOM die Druck-Stile (nur gleiche Herkunft),
 * inklusive per @import eingebundener Stylesheets. info.restore() stellt die
 * ursprünglichen Media-Angaben wieder her.
 */
function emulatePrintMedia(doc) {
    const changes = [];
    const info = {
        sheets: 0,
        blocked: [],
        pageRules: [],
        imports: 0,
        restore() {
            changes.reverse().forEach(([mediaList, original]) => {
                try {
                    mediaList.mediaText = original;
                } catch (err) {
                    /* nicht änderbar */
                }
            });
            changes.length = 0;
        }
    };

    const rewrite = (mediaList) => {
        const original = mediaList.mediaText;
        const updated = mediaTextAsPrint(original);
        if (updated === original) return;
        mediaList.mediaText = updated;
        changes.push([mediaList, original]);
    };

    const walkSheet = (sheet) => {
        if (sheet.media && sheet.media.mediaText) rewrite(sheet.media);
        walk(sheet.cssRules);
        info.sheets++;
    };

    const walk = (rules) => {
        Array.from(rules || []).forEach((rule) => {
            if (rule.styleSheet) {
                info.imports++;
                try {
                    walkSheet(rule.styleSheet);
                } catch (err) {
                    info.blocked.push(rule.href || "(import)");
                }
                return;
            }
            if (rule.media && "cssRules" in rule) {
                try {
                    rewrite(rule.media);
                } catch (err) {
                    /* nicht änderbar */
                }
            }
            if (rule.type === 6 /* CSSRule.PAGE_RULE */) {
                info.pageRules.push(rule.cssText.slice(0, 200));
            }
            if (rule.cssRules) walk(rule.cssRules);
        });
    };

    Array.from(doc.styleSheets).forEach((sheet) => {
        try {
            walkSheet(sheet);
        } catch (err) {
            info.blocked.push(sheet.href || "(inline)");
        }
    });

    return info;
}

/**
 * Versetzt das Druck-Iframe vorübergehend in einen druckgleichen Zustand:
 * Breite = bedruckbarer Seitenbereich, @media-print-Regeln aktiv. Messungen
 * (Positionen, Hintergrundgrößen, Dokumentbreite) entsprechen
 * dann dem Drucklayout statt dem Bildschirmlayout. Liefert eine Funktion, die
 * den ursprünglichen Zustand wiederherstellt.
 */
async function enterPrintLayoutEmulation(iframe, win) {
    const props = ["width", "min-width", "max-width"];
    const saved = props.map((prop) => [
        prop,
        iframe.style.getPropertyValue(prop),
        iframe.style.getPropertyPriority(prop)
    ]);

    setDebugFrameWidth(iframe, PRINT_PAGE_CONTENT_WIDTH_PX);
    const media = emulatePrintMedia(win.document);

    if (win.document.fonts && win.document.fonts.ready) {
        await win.document.fonts.ready.catch(() => {});
    }

    return () => {
        media.restore();
        saved.forEach(([prop, value, priority]) => {
            if (value) iframe.style.setProperty(prop, value, priority);
            else iframe.style.removeProperty(prop);
        });
    };
}

/**
 * Safari-Korrektur: Media Queries festschreiben, wie Chrome sie druckt.
 *
 * Chrome und Firefox werten Media Queries beim Drucken gegen den bedruckbaren
 * Seitenbereich aus (A4 mit 15 mm Rand ≈ 680 CSS-px). Safari wertet sie gegen
 * die bereits verbreiterte Druckseite aus – dadurch greifen Desktop-Breakpoints
 * (zweispaltiges Layout, größere Schriften, überbreite Tabellen).
 *
 * Jede Media-Query-Liste im Druck-Dokument wird deshalb einmal so ausgewertet,
 * wie Chrome sie beim Drucken sieht (Seitenbereich, Medientyp print), und
 * durch „all“ bzw. „not all“ ersetzt. Das Ergebnis hängt danach nicht mehr
 * davon ab, wie Safari die Druckseite bemisst.
 */
async function freezePrintMediaQueries(iframe, win) {
    const doc = win.document;
    const props = ["width", "min-width", "max-width", "height", "min-height", "max-height"];
    const saved = props.map((prop) => [
        prop,
        iframe.style.getPropertyValue(prop),
        iframe.style.getPropertyPriority(prop)
    ]);

    setDebugFrameWidth(iframe, PRINT_PAGE_CONTENT_WIDTH_PX);
    iframe.style.setProperty("height", `${PRINT_PAGE_CONTENT_HEIGHT_PX}px`, "important");
    iframe.style.setProperty("min-height", `${PRINT_PAGE_CONTENT_HEIGHT_PX}px`, "important");
    iframe.style.setProperty("max-height", "none", "important");
    void iframe.offsetWidth; // Layout erzwingen, damit das Iframe die neue Größe kennt

    const info = { frozen: 0, active: 0, blocked: [], viewport: [win.innerWidth, win.innerHeight] };

    const freeze = (mediaList) => {
        const original = mediaList && mediaList.mediaText;
        if (!original || original === "all" || original === "not all") return;

        const printText = mediaTextAsPrint(original);
        const matches = printText !== "not all" && win.matchMedia(printText).matches;

        mediaList.mediaText = matches ? "all" : "not all";
        info.frozen++;
        if (matches) info.active++;
    };

    const walk = (rules) => {
        Array.from(rules || []).forEach((rule) => {
            if (rule.styleSheet) {
                // @import: Media-Liste der Regel gilt für das importierte Sheet
                try {
                    freeze(rule.media);
                    walk(rule.styleSheet.cssRules);
                } catch (err) {
                    info.blocked.push(rule.href || "(import)");
                }
                return;
            }
            if (rule.media && "cssRules" in rule) {
                try {
                    freeze(rule.media);
                } catch (err) {
                    /* nicht änderbar */
                }
            }
            if (rule.cssRules) walk(rule.cssRules);
        });
    };

    try {
        Array.from(doc.styleSheets).forEach((sheet) => {
            try {
                freeze(sheet.media);
                walk(sheet.cssRules);
            } catch (err) {
                info.blocked.push(sheet.href || "(inline)");
            }
        });
    } finally {
        saved.forEach(([prop, value, priority]) => {
            if (value) iframe.style.setProperty(prop, value, priority);
            else iframe.style.removeProperty(prop);
        });
    }

    if (doc.fonts && doc.fonts.ready) {
        await doc.fonts.ready.catch(() => {});
    }

    return info;
}

/**
 * Dokumentbreite im (emulierten) Drucklayout: äußerste rechte Kante aller
 * Elemente. Genau diese Breite verkleinert Chrome auf die Seite.
 */
function measureDocumentWidth(win) {
    const doc = win.document;
    let maxRight = 0;

    doc.querySelectorAll("html, body, body *").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width + rect.height > 0) maxRight = Math.max(maxRight, rect.right + win.scrollX);
    });

    return Math.max(maxRight, doc.documentElement.scrollWidth);
}

/**
 * Kurzbezeichnung eines Elements (Tag, ID, bis zu drei Klassen).
 */
function shortElementName(el) {
    const classes = Array.from(el.classList || []).slice(0, 3).join(".");
    return `${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ""}${classes ? `.${classes}` : ""}`;
}

/**
 * Kurzbeschreibung eines Elements für den Diagnose-Bericht.
 */
function describeElement(el, win) {
    const cs = win.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    const round = (value) => Math.round(value * 100) / 100;

    return {
        el: shortElementName(el),
        left: round(rect.left + win.scrollX),
        right: round(rect.right + win.scrollX),
        width: round(rect.width),
        css: {
            display: cs.display,
            position: cs.position,
            width: cs.width,
            minWidth: cs.minWidth,
            maxWidth: cs.maxWidth,
            marginLeft: cs.marginLeft,
            marginRight: cs.marginRight,
            paddingLeft: cs.paddingLeft,
            paddingRight: cs.paddingRight,
            fontSize: cs.fontSize,
            overflowX: cs.overflowX
        }
    };
}

const DEBUG_KEY_SELECTORS = [
    "html",
    "body",
    "body > img",
    ".js-print",
    ".row",
    "[class*='col-']",
    ".newspaper",
    "table",
    ".js-print img",
    "h1",
    "p"
];

/**
 * Misst das (nachgebaute) Drucklayout bei der aktuellen Iframe-Breite.
 */
function measurePrintLayout(win, label) {
    const doc = win.document;
    const elements = Array.from(doc.querySelectorAll("html, body, body *"));

    const rows = elements
        .map((el) => {
            const rect = el.getBoundingClientRect();
            const marginRight = parseFloat(win.getComputedStyle(el).marginRight) || 0;
            return { el, right: rect.right + win.scrollX, marginRight, size: rect.width + rect.height };
        })
        .filter((row) => row.size > 0);

    const maxBorderRight = Math.max(...rows.map((row) => row.right));
    const maxMarginRight = Math.max(...rows.map((row) => row.right + Math.max(0, row.marginRight)));
    const byRight = [...rows].sort((a, b) => b.right - a.right);

    const key = {};
    DEBUG_KEY_SELECTORS.forEach((selector) => {
        const el = doc.querySelector(selector);
        if (el) key[selector] = describeElement(el, win);
    });

    return {
        label,
        viewportWidth: doc.documentElement.clientWidth,
        htmlScrollWidth: doc.documentElement.scrollWidth,
        bodyScrollWidth: doc.body ? doc.body.scrollWidth : null,
        maxBorderRight: Math.round(maxBorderRight * 100) / 100,
        maxMarginRight: Math.round(maxMarginRight * 100) / 100,
        key,
        widest: byRight.slice(0, 6).map((row) => describeElement(row.el, win)),
        marginBeyond: rows
            .filter((row) => row.right + row.marginRight > maxBorderRight + 0.5)
            .slice(0, 6)
            .map((row) => describeElement(row.el, win))
    };
}

/**
 * Setzt die Breite des Diagnose-Iframes – mit !important, damit Seiten-CSS
 * wie „iframe { max-width: 100% }“ sie nicht begrenzt.
 */
function setDebugFrameWidth(frame, width) {
    frame.style.setProperty("width", `${width}px`, "important");
    frame.style.setProperty("min-width", `${width}px`, "important");
    frame.style.setProperty("max-width", "none", "important");
}

/**
 * Lädt das Druck-Dokument unsichtbar in Seitenbreite und aktiviert die
 * @media-print-Regeln – ein Nachbau des Layouts, das der Browser beim Drucken
 * zuerst berechnet. Aufrufer müssen frame.remove() aufrufen.
 */
async function createPrintEmulationFrame(html, widthPx = PRINT_PAGE_CONTENT_WIDTH_PX) {
    const frame = document.createElement("iframe");

    Object.assign(frame.style, {
        position: "fixed",
        left: "-10000px",
        top: "0",
        height: "1100px",
        border: "0",
        visibility: "hidden"
    });
    setDebugFrameWidth(frame, widthPx);
    frame.setAttribute("aria-hidden", "true");

    const loaded = new Promise((resolve) => {
        frame.onload = resolve;
    });

    frame.srcdoc = html;
    document.body.appendChild(frame);

    try {
        await loaded;
        const win = frame.contentWindow;
        await waitForPrintResources(win);
        const media = emulatePrintMedia(win.document);
        return { frame, win, media };
    } catch (err) {
        frame.remove();
        throw err;
    }
}

/**
 * Berechnet den Maßstab, mit dem Chrome das Dokument auf die Seite verkleinert.
 * Chrome teilt die (aufgerundete) Seitenbreite in px durch die gerundete
 * Dokumentbreite; ist das Dokument schmaler als die Seite, wird nicht skaliert.
 */
function chromeEquivalentPrintScale(documentWidthPx, pageWidthPx = PRINT_PAGE_CONTENT_WIDTH_PX) {
    const docWidth = Math.round(documentWidthPx);
    if (!(docWidth > pageWidthPx)) return 1;
    return Math.ceil(pageWidthPx) / docWidth;
}

/**
 * Korrektur 4 (Firefox): Druckmaßstab exakt wie in Chrome.
 *
 * Firefox berechnet „An Seitenbreite anpassen“ in einem eigenen Vorab-
 * Durchgang und kommt dabei auf eine etwas größere Dokumentbreite als Chrome
 * (hier 951,6 statt 945 px), obwohl im gedruckten Layout nichts so weit
 * reicht. Ergebnis: alles 0,8 % kleiner. Deshalb wird der Chrome-Maßstab
 * selbst berechnet und per CSS zoom auf <body> gesetzt – das Dokument passt
 * dann bereits auf die Seite und Firefox muss nichts mehr verkleinern.
 * Nebeneffekt: Die Seitenhöhe in CSS-px entspricht ebenfalls Chrome, die
 * Seitenumbrüche liegen also an denselben Stellen.
 */
function applyChromeEquivalentScale(win, documentWidth) {
    const doc = win.document;

    if (!(win.CSS && win.CSS.supports && win.CSS.supports("zoom", "0.5"))) {
        return { applied: false, reason: "CSS zoom wird nicht unterstützt" };
    }

    const scale = chromeEquivalentPrintScale(documentWidth);

    if (scale >= 1) {
        return { applied: false, documentWidth, scale };
    }

    // Zoom auf <body>, nicht auf <html>: Ein Zoom am Root-Element würde auch die
    // @page-Ränder mitskalieren. <html> verliert dafür seine feste A4-Breite
    // (210 mm), die sonst breiter als der bedruckbare Bereich wäre und Firefox'
    // eigene Verkleinerung erneut auslösen würde.
    doc.documentElement.style.setProperty("width", "auto", "important");
    doc.documentElement.style.setProperty("min-width", "0", "important");
    doc.body.style.setProperty("zoom", String(scale), "important");

    return { applied: true, documentWidth, scale };
}

/**
 * Baut das Druck-Dokument mit Seitenbreite nach und protokolliert die
 * Dokumentbreite, aus der der Browser den Verkleinerungsfaktor berechnet.
 */
async function logPrintLayoutDiagnostics(html, effectiveOptions = {}) {
    const pageWidthPx = PRINT_PAGE_CONTENT_WIDTH_PX;
    const pageBoxWidthPx = (PRINT_PAGE.widthMm * 96) / 25.4;
    const { frame, win, media } = await createPrintEmulationFrame(html, pageWidthPx);

    try {
        const report = {
            userAgent: navigator.userAgent,
            options: effectiveOptions,
            pageWidthPx: Math.round(pageWidthPx * 100) / 100,
            media,
            passes: []
        };

        report.passes.push(measurePrintLayout(win, "Seitenbreite"));

        const docWidth = Math.round(report.passes[0].maxBorderRight);
        report.chromeEquivalentScale = chromeEquivalentPrintScale(report.passes[0].maxBorderRight);

        if (docWidth > pageWidthPx + 1) {
            setDebugFrameWidth(frame, docWidth);
            report.passes.push(measurePrintLayout(win, `verbreitert auf ${docWidth}px`));

            const boxWidth = Math.round((pageBoxWidthPx * docWidth) / pageWidthPx);
            setDebugFrameWidth(frame, boxWidth);
            report.passes.push(measurePrintLayout(win, `Seitenbox skaliert ${boxWidth}px`));
        }

        console.log("[print-debug]", JSON.stringify(report));
        return report;
    } finally {
        frame.remove();
    }
}

/**
 * Eindeutige, von typischen Seitenfarben gut unterscheidbare Markierungsfarbe.
 * Alle Kanalwerte ≡ 2 (mod 12), Index 1 … 4095.
 */
function debugMarkerColor(index) {
    const channel = (value) => 38 + 12 * (value & 15);
    return [channel(index >> 8), channel(index >> 4), channel(index)];
}

/**
 * Versieht jedes Element des Druck-Dokuments mit einer 1-px-Outline in einer
 * eindeutigen Farbe (beeinflusst weder Layout noch Druckbreite) und schreibt
 * die Zuordnung Index → Element als „[print-debug-map]“ in die Konsole.
 */
function addPrintDebugMarkers(win) {
    const doc = win.document;
    const elements = [doc.documentElement, ...(doc.body ? [doc.body, ...doc.body.querySelectorAll("*")] : [])].filter(
        (el) => el.namespaceURI === XHTML_NS
    );

    const map = [];

    elements.slice(0, 4095).forEach((el, i) => {
        const [r, g, b] = debugMarkerColor(i + 1);
        el.style.setProperty("outline", `1px solid rgb(${r}, ${g}, ${b})`, "important");
        el.style.setProperty("outline-offset", "-1px", "important");
        map.push(shortElementName(el));
    });

    console.log(
        "[print-debug-map]",
        JSON.stringify({ userAgent: navigator.userAgent, colors: "channel = 38 + 12 * nibble", elements: map })
    );
}

/**
 * Erstellt ein vollständiges HTML-Dokument für den Druck, sammelt dafür die
 * auswählbaren Inhalte ein, entfernt nicht druckbare Elemente und ergänzt
 * Styles, Logo und optionale Seitenumbrüche.
 */
export function buildPrintableHtml({
                                       selector = ".js-print",
                                       hideSelector = ".js-print-hide",
                                       title = document.title || "Print",
                                       titleInfoSelector = ".weekly__info",
                                       titleInfoAttribute = "data-id",
                                       titleSeparator = "_",
                                       addPageBreaks = true,
                                       pageBreakClass = "print-page-break",
                                       lang = document.documentElement.lang || "de",
                                       desktopViewportWidth = 1920,
                                       imageScale = 0.6,
                                       breakBeforeRules = [
                                           // Erarbeitung: nur neue Seite, wenn es einen Einstieg gibt
                                           {
                                               id: "weekly-processing-1",
                                               onlyIfAnyExists: ["weekly-initial"]
                                           },
                                           // Anwendung | Vertiefung: nur neue Seite, wenn Einstieg oder Erarbeitung vorhanden ist
                                           {
                                               id: "weekly-conclusion-1",
                                               onlyIfAnyExists: ["weekly-initial", "weekly-processing-1"]
                                           },
                                           // Lösungen: immer neue Seite
                                           { id: "weekly-solutions" }
                                       ],
                                       breakBeforeClass = "print-break-before",
                                       keepTogetherIds = [
                                           "weekly-initial-tasks",
                                           "weekly-processing-tasks",
                                           "weekly-conclusion-tasks"
                                       ],
                                       keepTogetherClass = "print-keep-together"
                                   } = {}) {
    const nodes = Array.from(document.querySelectorAll(selector));

    if (!nodes.length) {
        throw new Error(`Keine Elemente gefunden für Selektor: ${selector}`);
    }

    const cssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map((link) => link.outerHTML)
        .join("\n");

    const styleTags = Array.from(document.querySelectorAll("style"))
        .map((style) => style.outerHTML)
        .join("\n");

    const baseTag = `<base href="${document.baseURI}">`;

    const finalTitle = resolvePrintTitle({
        title,
        titleInfoSelector,
        titleInfoAttribute,
        titleSeparator
    });

    const escapeHtml = (value) =>
        String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");

    // Das Logo wird separat eingefügt und bleibt in der Originalgröße (nicht von imageScale betroffen)
    const logoHtml = `
      <img
        class="mb--small"
        alt="Logo Buchner weekly"
        src="/userdata/images/weekly/basic/logo_ccb_bw-black.svg"
        style="width: 170px; height: 60px;"
      >
    `;

    const printableNodes = nodes.filter((node) => !node.matches(hideSelector));

    if (!printableNodes.length) {
        throw new Error(
            `Alle gefundenen Elemente wurden durch hideSelector ausgeschlossen: ${hideSelector}`
        );
    }

    const parts = printableNodes
        .map((node, i) => {
            const clone = node.cloneNode(true);

            forceDisplayNoneToBlock(node, clone);

            clone.querySelectorAll(hideSelector).forEach((el) => el.remove());
            clone.querySelectorAll("script, noscript").forEach((el) => el.remove());

            // Bilder auf den gewünschten Faktor verkleinern (Logo wird separat eingefügt und bleibt unberührt)
            scaleImages(clone, imageScale);

            // Bestimmte Abschnitte beginnen immer auf einer neuen Seite
            markSectionsForPageBreak(clone, breakBeforeRules, breakBeforeClass);

            // Aufgabenabschnitte nicht über zwei Seiten trennen
            markSectionsKeepTogether(clone, keepTogetherIds, keepTogetherClass);

            let html = clone.outerHTML;

            if (addPageBreaks && i < printableNodes.length - 1) {
                html += `<div class="${pageBreakClass}"></div>`;
            }

            return html;
        })
        .join("\n");

    const printExtras = `
      <style>
        @page {
          size: A4 portrait;
          margin: ${PRINT_PAGE.marginTopMm}mm ${PRINT_PAGE.marginRightMm}mm ${PRINT_PAGE.marginBottomMm}mm ${PRINT_PAGE.marginLeftMm}mm;
        }

        html,
        body {
          width: 210mm;
          margin: 0;
          padding: 0;
        }

        body.print-desktop {
          min-width: ${desktopViewportWidth}px;
        }

        .slidecontentItem__headline::after,
        .slidecontentItem__headline:after {
          content: none !important;
          display: none !important;
        }

        @media print {
          .${pageBreakClass} {
            break-after: page;
            page-break-after: always;
          }

          .${breakBeforeClass} {
            break-before: page;
            page-break-before: always;
          }

          /* Aufgabenabschnitte nicht über zwei Seiten teilen: passt der Abschnitt
             nicht mehr vollständig auf die Restseite, rutscht er auf die nächste */
          .${keepTogetherClass} {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Zeitungsartikel-Kästen (.newspaper) nicht über zwei Seiten teilen:
             würde der Kasten umbrochen, beginnt er komplett auf der nächsten Seite */
          .newspaper {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Tabellen nicht über zwei Seiten teilen: wird sie nicht mehr
             vollständig passen, rutscht sie komplett auf die nächste Seite */
          table,
          .table_wrapper {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Fallback für Tabellen, die länger als eine Seite sind:
             wenigstens Zeilen nicht zerschneiden */
          tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Überschrift nicht allein am Seitenende stehen lassen,
             wenn die folgende Tabelle auf die nächste Seite rutscht */
          h1, h2, h3, h4, h5, h6 {
            break-after: avoid;
            page-break-after: avoid;
          }
        }
      </style>
    `;

    return `<!doctype html>
<html lang="${lang}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=${desktopViewportWidth}, initial-scale=1" />
  ${baseTag}
  <title>${escapeHtml(finalTitle)}</title>
  ${cssLinks}
  ${styleTags}
  ${printExtras}
</head>
<body class="print-desktop">
  ${logoHtml}
  ${parts}
</body>
</html>`;
}

/**
 * Erzeugt aus den gewählten Elementen ein druckbares HTML, lädt es in ein
 * unsichtbares Iframe und startet anschließend den Browser-Druckdialog.
 *
 * Zusätzliche Optionen für Firefox (alle optional):
 *   firefoxFixes:                "auto" (Standard, nur in Firefox) | true | false
 *   firefoxFixGradients:         Verläufe mit Transparenz rastern (Standard: true)
 *   firefoxFixAbsolutePositions: absolut positionierte Elemente fixieren (Standard: true)
 *   firefoxDisableLigatures:     fi/fl-Ligaturen abschalten, damit Text im PDF
 *                                korrekt kopier- und durchsuchbar ist (Standard: true)
 *   firefoxRasterScale:          Auflösungsfaktor der gerasterten Verläufe (Standard: 2)
 *   firefoxMatchChromeScale:     Druckmaßstab wie Chrome berechnen und per CSS zoom
 *                                setzen, statt Firefox' eigener Anpassung (Standard: true)
 *   firefoxFixListBullets:       Aufzählungspunkte (disc) wie Chrome zeichnen (Standard: true)
 *
 * Zusätzliche Optionen für Safari/WebKit (alle optional):
 *   safariFixes:                 "auto" (Standard, nur in Safari/WebKit) | true | false
 *   safariFreezeMediaQueries:    Media Queries so festschreiben, wie Chrome sie beim
 *                                Drucken auswertet (Standard: true)
 *   safariCapTables:             überbreite Tabellen auf ihre max-width begrenzen,
 *                                wie Chrome es tut (Standard: true)
 *   safariTableColumns:          Spaltenbreiten wie Chrome verteilen (Standard: true)
 *   safariUnflexRows:            untereinander stehende Flex-Zeilen als Blöcke setzen,
 *                                damit WebKit sauber umbricht (Standard: true)
 *   safariFixGradients, safariFixAbsolutePositions, safariDisableLigatures,
 *   safariFixListBullets, safariRasterScale: wie die Firefox-Optionen
 *
 * Diagnose (alle Browser, nur zur Fehlersuche):
 *   printLayoutDebug:            true = Druckbreite messen, Elemente im PDF farbig
 *                                markieren und Berichte in die Konsole schreiben.
 *                                Alternativ ohne Codeänderung per Konsole:
 *                                localStorage.printLayoutDebug = "1"
 */
export async function printSelectorToPrinter(options = {}) {
    const desktopViewportWidth = options.desktopViewportWidth ?? 1920;
    const applyFirefoxFixes = shouldApplyFirefoxFixes(options.firefoxFixes ?? "auto");
    const applySafariFixes = !applyFirefoxFixes && shouldApplySafariFixes(options.safariFixes ?? "auto");

    const html = buildPrintableHtml({
        ...options,
        desktopViewportWidth
    });

    // Optional: Druckbreite diagnostizieren (nur zur Fehlersuche)
    const debugLayout = isPrintLayoutDebugEnabled(options);
    if (debugLayout) {
        try {
            await logPrintLayoutDiagnostics(html, {
                desktopViewportWidth,
                imageScale: options.imageScale ?? 0.6,
                selector: options.selector ?? ".js-print",
                firefoxFixes: options.firefoxFixes ?? "auto",
                applyFirefoxFixes,
                safariFixes: options.safariFixes ?? "auto",
                applySafariFixes
            });
        } catch (err) {
            console.warn("[print-debug] Diagnose fehlgeschlagen:", err);
        }
    }

    const iframe = document.createElement("iframe");

    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = `${desktopViewportWidth}px`;

    /**
     * Die Höhe begrenzt den Druckinhalt nicht.
     * Sie definiert nur die Viewport-Höhe des Iframes.
     * Der Inhalt selbst kann im Druck beliebig lang werden.
     */
    iframe.style.height = "1080px";

    iframe.style.border = "0";
    iframe.style.visibility = "hidden";
    iframe.setAttribute("aria-hidden", "true");

    // Load-Handler vor dem Einfügen registrieren, damit kein Ereignis verloren geht
    const loaded = new Promise((resolve) => {
        iframe.onload = resolve;
    });

    iframe.srcdoc = html;
    document.body.appendChild(iframe);

    await loaded;

    /**
     * Kurze Wartezeit, damit Styles, Fonts und Layout im Iframe angewendet werden.
     */
    await new Promise((resolve) => setTimeout(resolve, 100));

    const w = iframe.contentWindow;

    /**
     * Firefox und Safari: bekannte Druckfehler korrigieren (schwarze Verlaufs-
     * flächen, verschobene Nummern-Kreise, Ligaturen, Aufzählungspunkte, dazu
     * Druckmaßstab in Firefox bzw. Media Queries in Safari).
     * In Chrome läuft dieser Block nicht – die Ausgabe bleibt dort unverändert.
     */
    const engine = applyFirefoxFixes ? "firefox" : applySafariFixes ? "safari" : null;

    if (engine) {
        // Engine-spezifische Option lesen, z. B. firefoxFixGradients / safariFixGradients
        const opt = (name, fallback) => options[`${engine}${name}`] ?? fallback;
        const matchChromeScale = engine === "firefox" && opt("MatchChromeScale", true);
        let documentWidth = null;

        try {
            await waitForPrintResources(w);

            // Safari: Media Queries so festschreiben, wie Chrome sie druckt
            if (engine === "safari" && opt("FreezeMediaQueries", true)) {
                const frozen = await freezePrintMediaQueries(iframe, w);
                if (debugLayout) console.log("[print-debug-media]", JSON.stringify(frozen));
            }

            // Safari: Druck-CSS (Bilder nicht teilen, keine Scrollbalken)
            if (engine === "safari") {
                const style = w.document.createElement("style");
                style.setAttribute(FX_STYLE_ATTR, "");
                style.textContent = SAFARI_PRINT_CSS;
                (w.document.head || w.document.documentElement).appendChild(style);
            }

            // Alle Messungen im druckgleichen Zustand (Seitenbreite, @media print)
            const restoreLayout = await enterPrintLayoutEmulation(iframe, w);
            try {
                // Safari: Flex-Zeilen als Blöcke, damit WebKit Inhalte nicht abschneidet
                if (engine === "safari" && opt("UnflexRows", true)) {
                    const unflexed = unflexStackedRows(w);
                    if (debugLayout) console.log("[print-debug-unflex]", unflexed);
                }

                // Safari: überbreite Tabellen wie Chrome auf max-width begrenzen …
                if (engine === "safari" && opt("CapTables", true)) {
                    const capped = capTablesToMaxWidth(w);
                    if (debugLayout) console.log("[print-debug-tables]", capped);
                }

                // … und die Spaltenbreiten wie Chrome verteilen
                if (engine === "safari" && opt("TableColumns", true)) {
                    const tables = layoutTablesLikeChrome(w);
                    if (debugLayout) console.log("[print-debug-columns]", tables);
                }

                documentWidth = measureDocumentWidth(w);
                await applyFirefoxPrintFixes(w, {
                    fixGradients: opt("FixGradients", true),
                    fixAbsolutePositions: opt("FixAbsolutePositions", true),
                    disableLigatures: opt("DisableLigatures", true),
                    fixListBullets: opt("FixListBullets", true),
                    rasterScale: opt("RasterScale", 2)
                });
            } finally {
                restoreLayout();
            }

        } catch (err) {
            console.warn("Druckkorrekturen konnten nicht vollständig angewendet werden:", err);
        }

        // Firefox: Druckmaßstab wie in Chrome (statt eigener Seitenbreiten-Anpassung)
        if (matchChromeScale && documentWidth) {
            try {
                const result = applyChromeEquivalentScale(w, documentWidth);
                if (debugLayout) console.log("[print-debug-scale]", JSON.stringify(result));
            } catch (err) {
                console.warn("Druckmaßstab konnte nicht angeglichen werden:", err);
            }
        }
    }

    // Debug: Haarlinien-Markierungen für die Auswertung des PDFs
    if (debugLayout) {
        try {
            addPrintDebugMarkers(w);
        } catch (err) {
            console.warn("[print-debug] Markierungen fehlgeschlagen:", err);
        }
    }

    /**
     * Der Browser schlägt beim PDF-Speichern den Titel des Haupt-Dokuments vor,
     * nicht den des Iframes. Deshalb wird document.title nur für die Dauer des
     * Drucks auf den gewünschten Dateinamen gesetzt und danach zurückgesetzt.
     */
    const originalTitle = document.title;
    let titleRestored = false;

    const restoreTitle = () => {
        if (titleRestored) return;
        titleRestored = true;
        document.title = originalTitle;
        window.removeEventListener("afterprint", restoreTitle);
        w.removeEventListener("afterprint", restoreTitle);
    };

    document.title = resolvePrintTitle(options);
    window.addEventListener("afterprint", restoreTitle);
    w.addEventListener("afterprint", restoreTitle);

    try {
        w.focus();
        w.print();
    } catch (err) {
        restoreTitle();
        throw err;
    }

    // Fallback, falls "afterprint" nicht ausgelöst wird
    setTimeout(restoreTitle, 1000);
    setTimeout(() => iframe.remove(), 1000);
}

let _nativePrint = null;
let _isIntercepted = false;
let _printShortcutHandler = null;

/**
 * Prüft, ob ein Tastaturereignis dem üblichen Druck-Shortcut wie Strg+P
 * oder Cmd+P entspricht.
 */
function isPrintShortcut(event) {
    const key = String(event.key || "").toLowerCase();
    return (event.ctrlKey || event.metaKey) && key === "p";
}

/**
 * Fängt den Druck-Shortcut auf Dokumentebene ab und leitet ihn auf die
 * aktuelle window.print-Implementierung um; gibt eine Funktion zum Entfernen
 * des Handlers zurück.
 */
export function blockPrintShortcut() {
    if (_printShortcutHandler) {
        return function unblockPrintShortcut() {
            if (_printShortcutHandler) {
                document.removeEventListener("keydown", _printShortcutHandler, true);
                _printShortcutHandler = null;
            }
        };
    }

    _printShortcutHandler = async function handlePrintShortcut(event) {
        if (!isPrintShortcut(event)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        try {
            await window.print();
        } catch (err) {
            console.error("Shortcut print failed:", err);
        }
    };

    document.addEventListener("keydown", _printShortcutHandler, true);

    return function unblockPrintShortcut() {
        if (_printShortcutHandler) {
            document.removeEventListener("keydown", _printShortcutHandler, true);
            _printShortcutHandler = null;
        }
    };
}

/**
 * Ersetzt window.print durch eine eigene Druckroutine für definierte Selektoren
 * und fällt auf die native Druckfunktion zurück, wenn kein passender Inhalt
 * gefunden wird oder ein Fehler auftritt.
 */
export function interceptWindowPrint(printOptions = {}) {
    if (!_nativePrint) {
        _nativePrint = window.print.bind(window);
    }

    if (_isIntercepted && window.print && window.print.__isIntercepted) {
        window.print.__printOptions = {
            ...window.print.__printOptions,
            ...printOptions,
            desktopViewportWidth: printOptions.desktopViewportWidth ?? 1920
        };

        return window.print.__restore;
    }

    const unblockShortcut = blockPrintShortcut();

    async function interceptedPrint() {
        const opts = interceptedPrint.__printOptions || {};
        const selector = opts.selector ?? ".js-print";

        if (!document.querySelector(selector)) {
            _nativePrint();
            return;
        }

        try {
            await printSelectorToPrinter({
                ...opts,
                desktopViewportWidth: opts.desktopViewportWidth ?? 1920
            });
        } catch (err) {
            console.error("Intercepted print failed -> fallback to native print:", err);
            _nativePrint();
        }
    }

    interceptedPrint.__isIntercepted = true;
    interceptedPrint.__printOptions = {
        ...printOptions,
        desktopViewportWidth: printOptions.desktopViewportWidth ?? 1920
    };

    interceptedPrint.__restore = function restore() {
        window.print = _nativePrint;
        _isIntercepted = false;
        unblockShortcut();
    };

    window.print = interceptedPrint;
    _isIntercepted = true;

    return interceptedPrint.__restore;
}